import ErrorIcon from "@/assets/fallbacks/error-img.png";
import { Button } from "@/components/ui/button";
import { useEditorStore } from "@/features/studies/stores/use-editor-store";
import { useLocation } from "wouter";
import { getErrorStatus } from "@/utils/get-error-status";

/* Prop Types */
interface ErrorProp {
  error?: unknown;
  onRefresh?: () => void;
  resetError?: () => void;
  showHomeBtn?: boolean;
}

/*
 * Error component
 * generic error fallback component
 */
export const Error = ({ error, onRefresh, resetError, showHomeBtn = true }: ErrorProp) => {
  /* Hooks */
  const [, navigate] = useLocation();

  /* Actions */
  const { reset } = useEditorStore((state) => state.editorActions);

  /* Handlers */
  const handleRefreshPage = () => {
    // if resetError function is provided, call it
    if (resetError) resetError();

    // if onRefresh handler is provided, call it
    if (onRefresh) onRefresh();

    // Reset all study state
    reset();

    // navigate to home page if no refresh handler is provided
    navigate("/");
  };

  // Default title and message
  let title = "Looks like we’ve run into some issues";
  let message = "It’s not you, it’s us! We are having some technical problems. Try refreshing the page or contact support if the issue persists via support@dragonflyai.co";

  if (error) {
    // Try to extract status from common error shapes
    const status = getErrorStatus(error);
    if (status === 404) {
      title = "Page not found (404)";
      message =
        "Looks like you are trying to access a page that doesn’t exist or the link has expired. Please check if the address is correct or contact support@dragonflyai.co for help.";
    } else if (status === 401 || status === 403) {
      title = "Access denied";
      message = "You do not have permission to view this resource. Please check you're signed in with the correct account or contact support@dragonflyai.co for help.";
    }
  }

  /* Render */
  return (
    <div className="flex flex-col grow w-full h-full items-center justify-center">
      <div className="flex flex-col gap-4 max-w-lg w-full items-center">
        <img src={ErrorIcon} className="size-32" />
        <div className="flex flex-col gap-3 items-center">
          <h2>{title}</h2>
          <p className="text-base-content/70 text-center">{message}</p>
        </div>
        <div className="flex gap-2">
          {onRefresh && <Button onClick={handleRefreshPage}>Refresh page</Button>}
          {showHomeBtn && <Button onClick={handleRefreshPage}>Go to Home</Button>}
        </div>
      </div>
    </div>
  );
};
